import { useEffect, useCallback, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface UseUnsavedChangesOptions {
  when: boolean;
  message?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

/**
 * Hook to prevent navigation when there are unsaved changes
 * Shows confirmation dialog before allowing user to leave
 *
 * Works with standard BrowserRouter without requiring data router
 *
 * @example
 * const { showDialog, confirmNavigation, cancelNavigation } = useUnsavedChanges({
 *   when: hasUnsavedChanges,
 *   message: 'You have unsaved changes. Are you sure you want to leave?'
 * })
 */
export function useUnsavedChanges({
  when,
  message = "You have unsaved changes. Are you sure you want to leave?",
  onConfirm,
  onCancel,
}: UseUnsavedChangesOptions) {
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<
    (() => void) | null
  >(null);
  const location = useLocation();
  const navigate = useNavigate();
  const shouldBlockRef = useRef(when);

  // Update the blocking ref when condition changes
  useEffect(() => {
    shouldBlockRef.current = when;
  }, [when]);

  // Handle browser beforeunload event (refresh/close tab)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldBlockRef.current) {
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [message]);

  // Handle back/forward navigation
  useEffect(() => {
    if (!when) return;

    let isBlocking = true;

    const handlePopState = () => {
      if (isBlocking && shouldBlockRef.current) {
        // Prevent navigation by pushing current state back
        window.history.pushState(
          null,
          "",
          location.pathname + location.search + location.hash,
        );

        // Show confirmation dialog
        setShowDialog(true);
        setPendingNavigation(() => () => {
          isBlocking = false;
          window.history.back();
        });
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      isBlocking = false;
    };
  }, [when, location]);

  const confirmNavigation = useCallback(() => {
    setShowDialog(false);
    onConfirm?.();

    // Execute pending navigation if exists
    if (pendingNavigation) {
      shouldBlockRef.current = false;
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation, onConfirm]);

  const cancelNavigation = useCallback(() => {
    setShowDialog(false);
    setPendingNavigation(null);
    onCancel?.();
  }, [onCancel]);

  // Custom navigate wrapper that shows dialog if blocking
  const navigateWithConfirm = useCallback(
    (to: string | number) => {
      if (shouldBlockRef.current) {
        setShowDialog(true);
        setPendingNavigation(() => () => {
          shouldBlockRef.current = false;
          if (typeof to === "number") {
            navigate(to);
          } else {
            navigate(to);
          }
        });
      } else {
        if (typeof to === "number") {
          navigate(to);
        } else {
          navigate(to);
        }
      }
    },
    [navigate],
  );

  return {
    showDialog,
    confirmNavigation,
    cancelNavigation,
    message,
    navigateWithConfirm,
  };
}

/**
 * Simpler hook for just showing native browser warning
 * Use this for quick implementation without custom dialog
 */
export function useBeforeUnload(when: boolean, message?: string) {
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (when) {
        event.preventDefault();
        event.returnValue = message || "";
        return message || "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [when, message]);
}
